/**
 * Company Service
 * Centralized service for company-related operations
 */

export interface CreateCompanyRequest {
  companyId: string;
  userId: string;
  name: string;
  email: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
  };
  phone?: string;
  wikiPayitSettings?: {
    tokenizationKey: string;
    securityKey: string;
    username?: string;
  };
  invoiceSettings?: {
    prefix?: string;
    suffix?: string;
    yearInNumber?: boolean;
  };
}

export interface CreateCompanyResponse {
  success: boolean;
  companyId: string;
  message: string;
}

/**
 * Create a new company with secure default email settings
 * Email settings (sendgridApiKey, fromEmail) are loaded from server environment
 *
 * @param data - Company creation data
 * @returns Promise with company creation result
 * @throws Error if company creation fails
 */
export async function createCompany(data: CreateCompanyRequest): Promise<CreateCompanyResponse> {
  try {
    const response = await fetch('/api/companies/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`
      }));
      throw new Error(errorData.error || 'Failed to create company');
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('[Company Service] Error creating company:', error);
    throw error;
  }
}

/**
 * Default WikiPayit settings for new companies
 */
export const DEFAULT_WIKIPAYIT_SETTINGS = {
  tokenizationKey: 'T3qmAr-ed6Yz9-DC3tCN-Y7nGfy',
  securityKey: 'JvMr9YHy6QDA2v2pjSJT9dA2r3u638xz',
  username: 'wikipayit',
};
